import {
  BRIDGE_LINK_EVENT_NAMES,
  BRIDGE_LINK_FAILED_EVENT_NAMES,
  BRIDGE_LINK_FAILURE_KIND,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  EVENT_SEVERITY
} from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';

/**
 * Stage-4 bridge-link rename group (ENG-4311, plan R-bridge): the canonical
 * `bridge_link_*` names replace the legacy `auto_link_bridge_*` family and
 * `bridge_connection_unavailable` replaces `bridge_goes_offline`. Retired pairs emit
 * only the canonical name and keep their historical catalog mapping.
 *
 * The name selection lives here at the domain boundary rather than at the controller
 * call sites, so each retirement changes one shared sender.
 * Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global);
 * mirrors the proven boot-lifecycle (ENG-4306) and auth-journey (ENG-4348) shapes.
 */

interface BridgeEventProperties extends EventProperties {
  bridgeType?: 'local' | 'secondary';
  agentId?: string;
  agent_id?: string;
  studioId?: string;
  studio_id?: string;
}

interface BridgeUpdateProperties extends BridgeEventProperties {
  error_message?: string;
}

interface BridgeTrayOpenProperties extends EventProperties {
  surface: string;
}

interface BridgeRestartProperties extends BridgeEventProperties {
  result: 'success' | 'failure';
  error_message?: string;
}

interface AutoLinkProperties extends EventProperties {
  room_id?: string;
  request_id?: string;
  // cause describes why the link failed (e.g. 'bridge_link_failed', 'server_check_failed')
  cause?: string;
  reason?: string;
  error_message?: string;
  // 'housekeeping' (ENG-4448): a self-healing outcome the product treats as
  // benign — NOT a real link failure — so it can opt out of the default
  // 'failure' result without losing the row (or its `reason`) for anyone who
  // still wants the raw, uncorrected count.
  result?: 'success' | 'failure' | 'housekeeping';
}

/**
 * Per-outcome narrowings that make canonical-event-map.json's required_properties
 * a COMPILE-time contract, not just a runtime one the e2e matcher discovers. A call
 * site that omits the required key now fails tsc instead of shipping an event the
 * canonical matcher will reject. Only the outcomes ENG-4311 renamed are narrowed —
 * `disabled` / `manual_fallback` keep the loose shape they had.
 */
interface AutoLinkAttemptProperties extends AutoLinkProperties {
  request_id: string;
}

interface AutoLinkSuccessProperties extends AutoLinkProperties {
  room_id: string;
}

/** Failure, error, timeout, empty and retry all declare `reason` required. */
interface AutoLinkOutcomeProperties extends AutoLinkProperties {
  reason: string;
}

interface BridgeConnectionUnavailableProperties extends EventProperties {
  studioId?: string;
  // Required: the map declares agent_id a required_property, and the context
  // resolver derives that canonical key from this one.
  agentId: string;
  agentName?: string;
  vendorName?: string;
  // ENG-4219 (coordinated with ENG-3775): retry-exhaustion call sites report
  // the failure as this HANDLED event with the error cause attached — never
  // an unhandled exception (ENG-4543 cleaned those up). `cause` is a
  // machine-readable reason (e.g. 'start_signal_exhausted'); `error_message`
  // carries the underlying error. Optional so the watchdog's transport-silence
  // emission (which has no error object) keeps its envelope unchanged.
  cause?: string;
  error_message?: string;
}

const normalizeBridgeIdentityProperties = (properties: BridgeEventProperties): EventProperties => ({
  bridgeType: properties.bridgeType ?? 'local',
  ...((properties.agent_id ?? properties.agentId)
    ? { agent_id: properties.agent_id ?? properties.agentId }
    : {}),
  ...((properties.studio_id ?? properties.studioId)
    ? { studio_id: properties.studio_id ?? properties.studioId }
    : {})
});

const normalizeBridgeProperties = ({
  bridgeType: _bridgeType,
  agentId: _agentId,
  agent_id: _agent_id,
  studioId: _studioId,
  studio_id: _studio_id,
  ...properties
}: BridgeEventProperties): EventProperties => {
  const normalizedIdentityProperties = normalizeBridgeIdentityProperties({
    ...(_bridgeType ? { bridgeType: _bridgeType } : {}),
    ...(_agentId ? { agentId: _agentId } : {}),
    ...(_agent_id ? { agent_id: _agent_id } : {}),
    ...(_studioId ? { studioId: _studioId } : {}),
    ...(_studio_id ? { studio_id: _studio_id } : {})
  });

  return {
    ...properties,
    ...normalizedIdentityProperties
  };
};

export const createBridgeLifecycleTracker = (track: AnalyticsTrackFn) => ({
  trackBridgeTrayOpened: ({ surface, ...properties }: BridgeTrayOpenProperties): void => {
    track(EVENT_NAMES.USER_OPEN_BRIDGE_TRAY, EVENT_CATEGORIES.USER, {
      ...properties,
      surface
    });
  },

  trackBridgeUpdateStarted: (properties: BridgeUpdateProperties): void => {
    track(EVENT_NAMES.BRIDGE_UPDATE_STARTED, EVENT_CATEGORIES.BRIDGE, {
      ...normalizeBridgeProperties(properties),
      result: 'started'
    });
  },

  trackBridgeUpdateCompleted: (properties: BridgeUpdateProperties): void => {
    track(EVENT_NAMES.BRIDGE_UPDATE_COMPLETED, EVENT_CATEGORIES.BRIDGE, {
      ...normalizeBridgeProperties(properties),
      result: 'success'
    });
  },

  trackBridgeUpdateFailed: ({ error_message, ...properties }: BridgeUpdateProperties): void => {
    track(EVENT_NAMES.BRIDGE_UPDATE_FAILED, EVENT_CATEGORIES.BRIDGE, {
      ...normalizeBridgeProperties(properties),
      result: 'failure',
      ...(error_message ? { error_message } : {})
    });
  },

  trackBridgeRestarted: ({
    result,
    error_message,
    ...properties
  }: BridgeRestartProperties): void => {
    track(EVENT_NAMES.RESTART_BRIDGE, EVENT_CATEGORIES.BRIDGE, {
      ...normalizeBridgeProperties(properties),
      result,
      ...(error_message ? { error_message } : {}),
      successfulRestart: result === 'success'
    });
  },

  trackAutoLinkAttempt: (properties: AutoLinkAttemptProperties): void => {
    for (const name of BRIDGE_LINK_EVENT_NAMES.ATTEMPT) {
      track(name, EVENT_CATEGORIES.BRIDGE, properties);
    }
  },

  trackAutoLinkSuccess: (properties: AutoLinkSuccessProperties): void => {
    const envelope = { ...properties, result: 'success' };
    for (const name of BRIDGE_LINK_EVENT_NAMES.SUCCEEDED) {
      track(name, EVENT_CATEGORIES.BRIDGE, envelope);
    }
  },

  // MERGE leg 1 of 2: handled link failures emit only the canonical name.
  // `failure_kind` preserves the distinction from the retired error leg.
  trackAutoLinkFailure: (properties: AutoLinkOutcomeProperties): void => {
    const envelope = {
      ...properties,
      result: properties.result ?? 'failure',
      failure_kind: BRIDGE_LINK_FAILURE_KIND.LINK_FAILURE
    };
    for (const name of BRIDGE_LINK_FAILED_EVENT_NAMES.LINK_FAILURE) {
      track(name, EVENT_CATEGORIES.BRIDGE, envelope);
    }
  },

  // "No devices found yet" is expected, so S3/warn keeps it out of blocking alerts.
  trackAutoLinkNoDevices: (properties: AutoLinkOutcomeProperties): void => {
    // Per rule: think-in-systems (ai.knowledge/global) — one retirement boundary.
    for (const name of BRIDGE_LINK_EVENT_NAMES.EMPTY) {
      track(name, EVENT_CATEGORIES.BRIDGE, {
        ...properties,
        severity: EVENT_SEVERITY.S3_WARN
      });
    }
  },

  // MERGE leg 2 of 2: an unexpected thrown error the controller caught. It now
  // emits only the canonical name, with `failure_kind='link_error'` preserving
  // the old stream's meaning. Mirrors
  // trackAutoLinkFailure's override (ENG-4448): most callers are real errors
  // and get the 'failure' default, but a self-healing outcome (stale_link_cleared,
  // the network-unavailable checking skip) passes its own `result` so it stops
  // counting against the failure rate without losing the row.
  trackAutoLinkError: (properties: AutoLinkOutcomeProperties): void => {
    const envelope = {
      ...properties,
      result: properties.result ?? 'failure',
      failure_kind: BRIDGE_LINK_FAILURE_KIND.LINK_ERROR
    };
    for (const name of BRIDGE_LINK_FAILED_EVENT_NAMES.LINK_ERROR) {
      track(name, EVENT_CATEGORIES.BRIDGE, envelope);
    }
  },

  trackAutoLinkRetry: (properties: AutoLinkOutcomeProperties): void => {
    for (const name of BRIDGE_LINK_EVENT_NAMES.RETRY) {
      track(name, EVENT_CATEGORIES.BRIDGE, properties);
    }
  },

  trackAutoLinkTimeout: (properties: AutoLinkOutcomeProperties): void => {
    const envelope = { ...properties, result: 'failure' };
    // Per think-in-systems, this shared tuple is the only timeout retirement point.
    for (const name of BRIDGE_LINK_EVENT_NAMES.TIMEOUT) {
      track(name, EVENT_CATEGORIES.BRIDGE, envelope);
    }
  },

  // Runtime reachability loss AFTER a successful link (bridge watchdog), not a
  // link-attempt outcome, hence its own root.
  trackBridgeConnectionUnavailable: (properties: BridgeConnectionUnavailableProperties): void => {
    for (const name of BRIDGE_LINK_EVENT_NAMES.CONNECTION_UNAVAILABLE) {
      track(name, EVENT_CATEGORIES.BRIDGE, properties);
    }
  },

  trackAutoLinkDisabled: (properties: AutoLinkProperties): void => {
    track(EVENT_NAMES.AUTO_LINK_BRIDGE_DISABLED, EVENT_CATEGORIES.BRIDGE, properties);
  },

  trackAutoLinkManualFallback: (properties: AutoLinkProperties): void => {
    track(EVENT_NAMES.AUTO_LINK_BRIDGE_MANUAL_FALLBACK, EVENT_CATEGORIES.BRIDGE, properties);
  }
});
